/**
 * @fileoverview Agent definition for iterative-refinement-v2.
 *
 * This is a COMPOSITE agent that orchestrates a critique-judge-refine loop:
 * 1. critique-committee-v2: Runs all 3 critics in parallel to analyze the draft
 * 2. draft-judge-v2: Evaluates the critique results and decides if satisfied
 * 3. run-refine-full-v2: Synthesizes findings and refines the draft (if not satisfied)
 *
 * The loop continues until the draft-judge-v2 is satisfied or max iterations (3) is reached.
 *
 * @category narrative.prose.revision
 * @type composite
 */

import type { AppAgentDefinition } from '@/domains/agents/schemas';
import { InputSchema, OutputSchema } from './schema';
// =============================================================================
// AGENT DEFINITION
// =============================================================================

export const definition: AppAgentDefinition = {
  id: 'iterative-refinement-v2',
  kind: 'composite',
  name: 'Iterative Refinement',
  description: 'Automatically refines a chapter draft through a critique-judge-refine loop. Runs all critics, evaluates quality, and refines until standards are met.',
  category: 'writing',
  inputSchema: InputSchema,
  outputSchema: OutputSchema,

  // --- Metadata ---
  metadata: {
    status: 'active',
    type: 'composite',
    addedIn: '2026-01',
    lastUpdated: '2026-01',
    usedBy: [
      'use-iterative-refinement-v2.ts',
    ],
    // Composite agents specify which agents they orchestrate
    composedOf: [
      'critique-committee-v2',
      'draft-judge-v2',
      'run-refine-full-v2',
    ],
    // Per-step model configuration is managed via project-level stepOverrides
    // (configured in Settings > Orchestration)
    relatedAgents: [
      'critique-committee-v2',
      'critique-prose-flow',
      'critique-sensory-atmosphere',
      'critique-voice-emotion',
      'draft-judge-v2',
      'run-refine-full-v2',
      'refine-synthesizer',
      'draft-refiner-v2',
    ],
    taxonomyPath: 'narrative.prose.revision',
  },
};
